import { z } from 'zod';
import type { ContractSource, LegalTextRecord, ReserveEntry, SignatureSummary } from './contract';
import type { CompanyLegal, SaleChannel } from './legal';
import type { CatalogUnit } from './catalog';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');

/**
 * Portail client à distance (CDC §5). Le lien est `<adresse web>/portail/<tenant>.<secret>` :
 * le secret (32 octets aléatoires) est créé par l'appareil du commercial — même hors ligne —
 * et seul son SHA-256 est envoyé au serveur. Lien limité au dossier, expirable, révocable.
 */
export const PORTAL_DEFAULT_DAYS = 7;
export const PORTAL_MAX_DAYS = 30;
export const PORTAL_CHANNELS = ['sms', 'whatsapp', 'email', 'other'] as const;
export type PortalChannel = (typeof PORTAL_CHANNELS)[number];

export const portalCreateInput = z.object({
  dossierId: uuid,
  tokenHash: z.string().regex(/^[0-9a-f]{64}$/),
  expiresAt: z.string().datetime(),
  allowEdit: z.boolean().default(true),
  allowUpload: z.boolean().default(true),
  allowSign: z.boolean().default(true),
  channel: z.enum(PORTAL_CHANNELS).nullish().transform((v) => v ?? null),
});
export type PortalCreateInput = z.input<typeof portalCreateInput>;

export interface PortalLinkRecord {
  id: string;
  version: number;
  updatedAt: string;
  dossierId: string;
  allowEdit: boolean;
  allowUpload: boolean;
  allowSign: boolean;
  channel: PortalChannel | null;
  expiresAt: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
}

export function portalLinkState(l: Pick<PortalLinkRecord, 'revokedAt' | 'expiresAt'>, now = new Date()): 'active' | 'expired' | 'revoked' {
  if (l.revokedAt) return 'revoked';
  return new Date(l.expiresAt) <= now ? 'expired' : 'active';
}

/** Champs que le client peut compléter lui-même (coordonnées), jamais le reste. */
export const PORTAL_CLIENT_FIELDS = ['email', 'phone', 'addressLine1', 'addressLine2', 'postalCode', 'city'] as const;
const opt = (max: number) => z.string().trim().max(max).nullish().transform((v) => (v ? v : null));
export const portalClientInput = z
  .object({
    email: z.string().trim().email('E-mail invalide').max(200).nullish().or(z.literal('')).transform((v) => (v ? v.toLowerCase() : null)),
    phone: opt(30),
    addressLine1: opt(200),
    addressLine2: opt(200),
    postalCode: opt(10),
    city: opt(100),
  })
  .partial();

/** Ce que voit le client : jamais de commentaire, de commission, de donnée interne. */
export interface PortalView {
  company: { name: string; legal: CompanyLegal | null; logoUrl: string | null; primaryColor: string | null };
  link: { allowEdit: boolean; allowUpload: boolean; allowSign: boolean; expiresAt: string };
  dossier: {
    id: string;
    reference: string | null;
    typeLabel: string;
    status: string;
    /** prêt à signer : brouillon / à corriger, au moins un produit, pas encore signé */
    signable: boolean;
    siteAddressLine1: string | null;
    siteAddressLine2: string | null;
    sitePostalCode: string | null;
    siteCity: string | null;
    installWallTypeLabel: string | null;
    installAtHeight: boolean | null;
    installAccess: string | null;
    installHeightM: number | null;
    reserves: ReserveEntry[];
    saleChannel: SaleChannel;
    proEmployeeCount: number | null;
    proOutsideMainActivity: boolean | null;
    earlyStartRequested: boolean;
    signature: Pick<SignatureSummary, 'signerName' | 'signedAt' | 'method'> | null;
  };
  client: ContractSource['client'];
  lines: { id: string; sortOrder: number; label: string; model: string | null; unit: CatalogUnit; quantity: number; unitPriceHtCents: number; vatRateBp: number; discountHtCents?: number; compareAtPriceHtCents?: number; unitPriceTtcCents?: number; discountTtcCents?: number; optionSteps: string[] }[];
  /** textes PRÉSENTÉS pour ce dossier (cases selon leurs conditions) */
  terms: LegalTextRecord[];
  consents: LegalTextRecord[];
  privacy: LegalTextRecord | null;
  withdrawalForm: LegalTextRecord | null;
  /** modèles des réserves du dossier */
  reserveTexts: LegalTextRecord[];
  /** documents déposés par le client et bon de commande signé */
  files: { id: string; name: string; mime: string; sizeBytes: number; status: 'pending' | 'ready'; createdAt: string; source: 'app' | 'portal' }[];
  /** après signature : total, déjà réglé, solde, et liens de paiement carte en cours (§10) */
  payment: { totalCents: number; paidCents: number; balanceCents: number; links: { amountCents: number; isDeposit: boolean; url: string; expiresAt: string }[] } | null;
}

export interface NotificationRecord {
  id: string;
  version: number;
  updatedAt: string;
  kind: string;
  dossierId: string | null;
  title: string;
  body: string | null;
  createdAt: string;
  readAt: string | null;
  /** internal page to open (announcement, documents…) when there is no dossier */
  link?: string | null;
  /** pushed to the phone (important event) */
  push?: boolean;
}

/* ------------------------------------------------------------------ helpers */

const b64url = (bytes: Uint8Array) => {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
/** 32 octets aléatoires (CSPRNG fourni par la plateforme) → secret URL-safe. */
export const portalSecret = (random32: Uint8Array) => b64url(random32);
export const portalToken = (tenantId: string, secret: string) => `${tenantId}.${secret}`;
export function parsePortalToken(token: string): { tenantId: string; secret: string } | null {
  const m = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.([A-Za-z0-9_-]{40,64})$/i.exec(token);
  return m ? { tenantId: m[1]!.toLowerCase(), secret: m[2]! } : null;
}

/** Liens d'envoi d'un message prêt à l'emploi (SMS, WhatsApp, e-mail). `dialCode` ex. « +590 ». */
export function messageLinks(text: string, opts: { phone?: string | null; email?: string | null; dialCode?: string | null; subject: string }) {
  const digits = (opts.phone ?? '').replace(/\D/g, '');
  const cc = (opts.dialCode ?? '').replace(/\D/g, '');
  const intl = digits.startsWith('00') ? digits.slice(2) : digits.startsWith('0') && cc ? cc + digits.slice(1) : digits;
  return {
    text,
    sms: `sms:${opts.phone ? opts.phone.replace(/\s/g, '') : ''}?&body=${encodeURIComponent(text)}`,
    whatsapp: `https://wa.me/${intl}?text=${encodeURIComponent(text)}`,
    email: `mailto:${opts.email ?? ''}?subject=${encodeURIComponent(opts.subject)}&body=${encodeURIComponent(text)}`,
  };
}

/** Liens de partage du portail client. */
export function shareLinks(url: string, opts: { phone?: string | null; email?: string | null; dialCode?: string | null; company: string }) {
  const text = `${opts.company} : voici votre lien sécurisé pour compléter et signer votre dossier : ${url}`;
  return messageLinks(text, { ...opts, subject: `${opts.company} — votre dossier` });
}
